import useRunsheetViewModel from './RunsheetViewModel'
import { Link } from 'react-router-dom'
import CircleSpinner from '@components/shared/CircleSpinner'
import RunsheetDocumentsTable from './RunsheetDocumentsTable'
import AddOrCreateCollection from '@components/tract/AddOrCreateCollection'
import { UpsertRunsheet } from '@components/tract/UpsertRunsheet'
import { UploadFile } from '@components/shared/UploadFile'
import { UpsertDocument } from '@components/tract/UpsertDocument'
import ExportRunsheetDialog from '@components/shared/Dialogs/ExportRunsheetDialog'
import { Tooltip } from 'react-tooltip'
import ConfirmationModal from '@/components/shared/ConfirmationModal'
import {
    DOCUMENT_TYPES_MAP,
    MAIN_TAB,
    PARTY_CASING,
    RUNSHEET_HEADERS,
} from '@/constants'
import TableHeaderManager from '@/components/shared/TableHeaderManager/TableHeaderManager'
import UploadDocumentDialog from '@components/shared/Dialogs/UploadDocumentDialog'
import { DocumentViewerModal } from '@components/collection/DocumentViewerV2'
import {
    MdAssignmentTurnedIn,
    MdDelete,
    MdDisplaySettings,
    MdElectricBolt,
    MdFileOpen,
    MdIosShare,
    MdMap,
    MdMemory,
    MdContentCopy,
    MdOutlineFileCopy,
    MdTextFields,
    MdLink,
    MdLinkOff, MdLockPerson,
    MdCategory,
} from 'react-icons/md'
import PlotterModal from '@/components/DeedPlotter/PlotterModal'
import { AssignTracts } from '../AssignTracts'
import RunsheetLegalDescriptionModal from '@/components/legaldescription/RunsheetLegalDescriptionModal'
import { SelectRunsheetDialog } from '@/components/collection/SelectRunsheetDialog'
import { CopyRunsheet } from './CopyRunsheet'
import ProcessDocumentsModal from './ProcessDocumentsModal'
import RunsheetTab from './RunsheetTab'
import RunsheetAddToTabModal from './RunsheetAddToTabModal'
import { UpsertTract } from '../UpsertTract'
import ChainerLink from './ChainerLink'
import '../Tracts.css'
import DocumentCategorizationModal from '../DocumentCategorization/DocumentCategorizationModal'

// const log = new Logger('Runsheet')

export const Runsheet = () => {
    
    const vm = useRunsheetViewModel()
    
    const renderTract = () => {
        if (vm.tract?.id) {
            return <div className="flex items-center gap-2">
                <span>Tract: </span>
                <span>
                    <Link
                        className="cursor-pointer underline text-primary line-clamp-1"
                        to={`/tract/${vm.tract.id}/ownership?runsheetId=` +
                                `${vm.id}&runsheetTabId=${vm.activeTab}`}>
                        {vm.tract.name}
                    </Link>
                </span>
            </div>
        }
        
        return <div>
            <button className="cursor-pointer underline text-primary"
                onClick={() => vm.setShowAssignTract(true)}>
                Assign Tract
            </button>
        </div>
    }
    
    if (vm.error) return (
        <div className="RunsheetDetail">
            <div className="absolute-centered text-center">
                <div className="flex flex-col justify-center items-center gap-4">
                    <MdLockPerson className="text-8xl text-red-500/20" />
                    <p>{vm.error}</p>
                </div>
            </div>
        </div>
    )
    
    if (!vm.id || !vm.currentRunsheet || vm.currentRunsheet.id !== vm.id || vm.loading) return (
        <div className="RunsheetDetail">
            <CircleSpinner className="absolute-centered" />
        </div>
    )
    
    return (
        <div className="RunsheetDetail">
            <div className="flex flex-row items-center justify-between p-3">
                <div className="flex items-center gap-2 mr-2">
                    <div className="w-48 mr-2">
                        <div className="tooltip tooltip-right" data-tip={vm.currentRunsheet.name}>
                            <div className="text-xl line-clamp-1 break-all text-left">
                                {vm.currentRunsheet.name}
                            </div>
                        </div>
                        <div>
                            <div className="text-sm">
                                
                                <div>
                                    <span>Assigned To: </span>
                                    <span>
                                        {vm.currentRunsheet?.assignedTo?.[0]?.firstName ?? ''}
                                    </span>
                                </div>
                                {renderTract()}
                                <div>
                                    <span className="text-muted">
                                        Selected {vm.markedDocuments.length} of {vm.runsheetDocs?.length}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <ul className="menu bg-base-200 menu-horizontal rounded-md">
                        <li>
                            <button
                                data-tooltip-id="default-tip"
                                data-tooltip-content="Reorder and define which fields to show on the Runsheet"
                                onClick={() => vm.customizationRef.current.open()}>
                                <MdDisplaySettings /><span className="ribbon-menu-text">Customize View</span>
                            </button>
                        </li>
                        <div className="divider divider-horizontal m-0"></div>
                        <li {...vm.exportRunsheet}>
                            <button
                                data-tooltip-id="default-tip"
                                data-tooltip-content="Export Runsheet"
                                onClick={vm.handleExportRunsheetClick}
                                disabled={!vm.exportRunsheetAccess}>
                                <MdIosShare /><span className="ribbon-menu-text">Export Runsheet</span>
                            </button>
                        </li>
                        <div className="divider divider-horizontal m-0"></div>
                        <li>
                            <button
                                data-tooltip-id="default-tip"
                                data-tooltip-content="Copy Runsheet"
                                onClick={() => {
                                    vm.setCopyRunsheetModal(true)
                                    vm.setNewRunsheetName(`${vm.currentRunsheet.name} (copy)`)
                                }}>
                                <MdContentCopy /><span className="ribbon-menu-text">Copy Runsheet</span>
                            </button>
                        </li>
                        <div className="divider divider-horizontal m-0"></div>
                        <li>
                            <button
                                data-tooltip-id="default-tip"
                                data-tooltip-content={`Casing: ${PARTY_CASING[vm.currentRunsheet.partyCasing]
                                    || 'Original'}`}
                                onClick={() => {
                                    const casing = Object.keys(PARTY_CASING)
                                    const currCasing = vm.currentRunsheet.partyCasing || 'none'
                                    const nextIndex = (casing.indexOf(currCasing) + 1) % casing.length
                                    
                                    vm.setCurrentRunsheet({ ...vm.currentRunsheet, partyCasing: casing[nextIndex] })
                                }}>
                                <MdTextFields />
                                <span className="ribbon-menu-text">
                                    Casing: {PARTY_CASING[vm.currentRunsheet.partyCasing] || 'Original'}
                                </span>
                            </button>
                        </li>
                        <div className="divider divider-horizontal m-0"></div>
                        <li>
                            <button
                                data-tooltip-id="default-tip"
                                data-tooltip-content={vm.activeTab === MAIN_TAB
                                    ? 'Assign Tract' : 'Assign Tract to Tab'}
                                onClick={() => vm.setShowAssignTract(true)}>
                                <MdAssignmentTurnedIn />
                                <span className="ribbon-menu-text">Assign Tract</span>
                            </button>
                        </li>
                        <div className="divider divider-horizontal m-0"></div>
                        <li>
                            <button
                                data-tooltip-id="default-tip"
                                data-tooltip-content="Deed Plots"
                                onClick={() => vm.setShowPlot(true)}>
                                <MdMap /><span className="ribbon-menu-text">Deed Plots</span>
                            </button>
                        </li>
                    </ul>
                    <ul className="menu bg-base-200 menu-horizontal rounded-md">
                        {vm.currentTab?.id === MAIN_TAB && 
                        <li
                            data-tooltip-id="default-tip"
                            data-tooltip-content={!vm.markedDocuments.length 
                                ? 'Select documents to add to a tab' 
                                : ''}>
                            <button
                                data-tooltip-id="default-tip"
                                data-tooltip-content="Add documents to a tab"
                                disabled={!vm.markedDocuments.length}
                                onClick={() => vm.setShowAddToTabModal(true)}>
                                <MdLink /><span className="ribbon-menu-text">Add to Tab</span>
                            </button>
                        </li>}
                        {vm.currentTab?.id !== MAIN_TAB && <li>
                            <button
                                onClick={() => vm.removeDocsFromTab()}
                                data-tooltip-id="default-tip"
                                data-tooltip-content="Removes the selected documents from the current tab only"
                                disabled={!vm.markedDocuments.length}>
                                <MdLinkOff /><span className="ribbon-menu-text">Remove From Tab</span>
                            </button>
                        </li>}
                    </ul>
                </div>
                <div className="flex gap-2">
                    <ul className="menu dropdown dropdown-end bg-base-200
                        shadow menu-horizontal rounded-md">
                        <li role="button" tabIndex={0}>
                            <div {...vm.documentTo} disabled={!vm.documentToRunsheetAccess}>
                                <MdFileOpen /><span className="ribbon-menu-text">Add Documents</span>
                            </div>
                        </li>
                        <ul
                            tabIndex={0}
                            className="dropdown-content block menu shadow-sm bg-base-200 rounded-md w-44
                                max-xl:top-[46px] top-[94px] m-0">
                            <li><Link to="/collections">Search Database</Link></li>
                            <li>
                                <button onClick={() => vm.uploadRef.current?.open()}>
                                    Upload Documents
                                </button>
                            </li>
                        </ul>
                    </ul>
                    <ul className="menu bg-base-200 menu-horizontal rounded-md">
                        <li>
                            <button
                                data-test-id="Runsheet-ProcessDocsButton"
                                data-tooltip-id="default-tip"
                                data-tooltip-content="Processing the selected Documents will not override inputted data"
                                onClick={() => vm.setShowProcessModal(true)}
                                disabled={!vm.markedDocuments.length}>
                                <MdMemory /><span className="ribbon-menu-text">Process Documents</span>
                            </button>
                        </li>
                        <div className="divider divider-horizontal m-0"></div>
                        <li>
                            <button
                                data-test-id="Runsheet-CopyDocsButton"
                                data-tooltip-id="default-tip"
                                data-tooltip-content="Copy the documents values to another runsheet."
                                onClick={() => vm.setCopyDocumentsModal(true)}
                                disabled={!vm.markedDocuments.length}>
                                <MdOutlineFileCopy /><span className="ribbon-menu-text">Copy Documents</span>
                            </button>
                        </li>
                        <div className="divider divider-horizontal m-0"></div>
                        <li>
                            <button
                                data-test-id="Runsheet-UpdateDocsButton"
                                data-tooltip-id="default-tip"
                                data-tooltip-content="Updating selected document will not override inputted data"
                                onClick={vm.handleUpdateDocuments}
                                disabled={!vm.markedDocuments.length}>
                                <MdElectricBolt /><span className="ribbon-menu-text">Update Documents</span>
                            </button>
                        </li>
                        <div className="divider divider-horizontal m-0"></div>
                        {vm.activeTab === MAIN_TAB && vm.isCategorizationEnabled &&
                        <>
                            <li
                                data-tooltip-id="default-tip"
                                data-tooltip-content={vm.missingForChainer
                                    ? 'You have to fill Grantor/Grantee/Instrument Date to sort be enabled' 
                                    : ''}>
                                <button
                                    data-test-id="Runsheet-SortDocsButton"
                                    data-tooltip-id="default-tip"
                                    data-tooltip-content="Sorts documents to specific tabs based on instrument type"
                                    onClick={() => vm.setShowCategorizationModal(true)}
                                    disabled={vm.missingForChainer || vm.currentRunsheet.categorized}>
                                    <MdCategory />
                                    <span className="ribbon-menu-text">Sort Documents</span>
                                </button>
                            </li>
                            <div className="divider divider-horizontal m-0"></div>
                        </>}
                        <li>
                            <ChainerLink
                                runsheetId={vm.id}
                                runsheetDocs={vm.runsheetDocs}
                                currentTab={vm.currentTab} />
                        </li>
                    </ul>
                    <ul className="menu bg-base-200 menu-horizontal rounded-md">
                        <li>
                            <button
                                onClick={() => vm.setShowBulkDeleteDocs(true)}
                                data-test-id="Runsheet-RemoveDocsButton"
                                data-tooltip-id="default-tip"
                                data-tooltip-content="Removes the selected documents from the runsheet"
                                disabled={!vm.markedDocuments.length}>
                                <MdDelete /><span className="ribbon-menu-text">Remove Documents</span>
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
            
            <div className="w-full max-w-full overflow-x-auto h-[calc(100vh-230px)] max-h-[calc(100vh-230px)] ">
                {/* @todo some of these props can prob be moved into the child component */}
                <RunsheetDocumentsTable
                    runsheet={vm.currentRunsheet}
                    runsheetDocs={vm.runsheetDocs}
                    markedDocuments={vm.markedDocuments}
                    setMarkedDocuments={vm.setMarkedDocuments}
                    setEditRsDocJoin={vm.setEditRsDocJoin}
                    showHideAdd={vm.setShowAdd} />
            </div>
            <RunsheetTab runsheetId={vm.id} />
            <UploadFile
                id="upload-document"
                accept={{ 'application/pdf': [] }}
                onDrop={vm.onDrop}
                fieldName="document"
                hidden={true}
                ref={vm.uploadRef} />
            {vm.uploadDialogOpen && (
                <UploadDocumentDialog
                    open={vm.uploadDialogOpen}
                    setOpen={vm.setUploadDialogOpen}
                    files={vm.uploadedFiles}
                    setUpdateDocs={vm.setUpdateDocs}
                    collectionId={vm.collectionId}
                    runsheetId={vm.id}
                    runsheetTabId={vm.activeTab && vm.activeTab !== MAIN_TAB ? vm.activeTab : undefined} />
            )}
            {/* {showAssignToUser && (
                <AssignToUser
                    runsheetId={id}
                    showAdd={showAssignToUser}
                    showHideAdd={showHideAssignToUser}
                    selectedModelIds={runsheet.id ? [runsheet.id] : []}
                    assignedToIds={runsheet?.assignedTo?.map(({ id }) => id) || []}
                    which="Runsheet"
                    setIsAssigned={() => {}}
                />
            )} */}
            {vm.showAssignTract && (
                <AssignTracts
                    showAdd={vm.showAssignTract}
                    showHideAdd={vm.setShowAssignTract}
                    showCreateTract={vm.setShowCreateTract}
                    isRunsheetTab={vm.activeTab && vm.activeTab !== MAIN_TAB}
                    tractId={vm.tract?.id}
                    tabId={vm.activeTab}
                    runsheetId={vm.currentRunsheet.id} 
                    ownershipType={vm.ownershipType}
                    setOwnershipType={vm.setOwnershipType} />
            )}
            {vm.showCreateTract && (
                <UpsertTract
                    showAdd={vm.showCreateTract}
                    showHideAdd={vm.setShowCreateTract}
                    runsheetTabId={vm.activeTab}
                    runsheetId={vm.currentRunsheet.id} 
                    ownershipType={vm.ownershipType}
                    shouldAssign={true} />
            )}
            {vm.showEdit && (
                <UpsertRunsheet
                    showAdd={vm.showEdit}
                    showHideAdd={vm.setShowEdit}
                    initial={vm.currentRunsheet}
                    multipleTractsMode={!vm.tractId}
                    redirect={false} />
            )}
            {vm.showAdd && (
                <UpsertDocument
                    runsheetId={vm.id}
                    showAdd={vm.showAdd}
                    setShowAdd={vm.setShowAdd}
                    editRsDocJoin={vm.editRsDocJoin} />
            )}
            
            {vm.showAddCollection && (
                <AddOrCreateCollection
                    showAddCollection={vm.showAddCollection}
                    setShowAddCollection={vm.setShowAddCollection}
                    collectionId={vm.collectionId}
                    setCollectionId={vm.setCollectionId}
                    uploadedFiles={vm.uploadedFiles}
                    setUploadedFiles={vm.setUploadedFiles}
                    setShowUploadPopUp={vm.setUploadDialogOpen} />
            )}
            
            {vm.copyDocumentsModal && (
                
                <SelectRunsheetDialog
                    confirmButtonText={'Copy'}
                    open={vm.copyDocumentsModal}
                    setOpen={vm.setCopyDocumentsModal}
                    setCreateRunsheetDialogOpen={vm.setCreateRunsheetDialogOpen}
                    onSelectRunsheet={selectedRunsheet => {
                        if (selectedRunsheet?.id) {
                            vm.submitCopyDocuments(selectedRunsheet.id, vm.markedDocuments)
                        }
                        
                    }} />
            )}
            
            {vm.copyRunsheetModal && (
                <CopyRunsheet
                    copyRunsheetModal={vm.copyRunsheetModal}
                    modalHandler={() => vm.setCopyRunsheetModal(false)}
                    submitCopy={vm.submitCopyRunsheet}
                    newRunsheetName={vm.newRunsheetName}
                    setNewRunsheetName={vm.setNewRunsheetName}
                    copyTract={vm.copyTract}
                    setCopyTract={vm.setCopyTract}
                    copyChaining={vm.copyChaining}
                    setCopyChaining={vm.setCopyChaining} />
            )}
            
            <UpsertRunsheet
                showAdd={vm.createRunsheetDialogOpen}
                showHideAdd={vm.setCreateRunsheetDialogOpen}
                redirect={false}
                runsheetSaved={newRunsheet => {
                    vm.setCreateRunsheetDialogOpen(false)
                    vm.submitCopyDocuments(newRunsheet.id, vm.markedDocuments)
                    vm.setMarkedDocuments([])
                }} />
            
            <ExportRunsheetDialog documents={vm.runsheetDocs.map(r => r.document.id)} />
            
            <TableHeaderManager storeName="runsheetHeaders" headers={RUNSHEET_HEADERS} ref={vm.customizationRef} />
            
            <DocumentViewerModal />
            
            <Tooltip place="top" id="rs-doc-tooltip" render={({ content }) => {
                
                if (content) {
                    const docObj = JSON.parse(content)
                    
                    return (<>
                        <div><span className="font-bold">Collection:</span> {docObj.collectionName}</div>
                        <div><span className="font-bold">Document:</span> {docObj.documentName}</div>
                        {docObj.documentType &&
                            <div>
                                <span className="font-bold">Type:</span> {DOCUMENT_TYPES_MAP[docObj.documentType]}
                            </div>}
                        <div><span className="font-bold">Status:</span> {docObj.status}</div>
                    </>)
                }
            }} />
            
            <RunsheetLegalDescriptionModal
                setShowAdd={vm.setShowAdd}
                setEditRsDocJoin={vm.setEditRsDocJoin} />
            
            <RunsheetAddToTabModal
                runsheetId={vm.id}
                modalState={[vm.showAddToTabModal, vm.setShowAddToTabModal]}
                markedDocuments={vm.markedDocuments}
                setMarkedDocuments={vm.setMarkedDocuments} />
            
            <ConfirmationModal
                title="Remove documents"
                message={<>
                    <p>Are you sure you want to remove the selected documents?</p>
                    <p className="mt-2 text-sm">
                        <i>Note: These documents will only be removed from the current runsheet, not deleted.</i>
                    </p>
                </>}
                onConfirm={vm.removeDocumentsBulk}
                showModal={vm.showBulkDeleteDocs}
                showHideModal={vm.setShowBulkDeleteDocs}
                confirmButtonText="Remove"
                confirmButtonClass="btn-error" />
            
            <ProcessDocumentsModal
                markedDocumentsState={[vm.markedDocuments, vm.setMarkedDocuments]}
                showState={[vm.showProcessModal, vm.setShowProcessModal]}
                runsheetDocs={vm.runsheetDocs}
                runsheetId={vm.currentRunsheet.id} />
            
            <PlotterModal
                open={vm.showPlot}
                modalHandler={() => vm.setShowPlot(false)}
                runsheetName={vm.currentRunsheet.name}
                runsheetId={vm.currentRunsheet.id} />
            
            {vm.showCategorizationModal && <DocumentCategorizationModal
                showModal={vm.showCategorizationModal}
                setShowModal={vm.setShowCategorizationModal} />}
        </div>
    )
    
}
